/**
 * Constructor Active Facets Component
 *
 * Displays currently applied filters with remove functionality.
 * Updates automatically when filters change.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { escapeHtml, getColorSwatchStyle, isColorFacetField } from '@theme/constructor-utils';

const FILTER_PARAM_PREFIX = 'filter.';
const FILTER_PARAM_PREFIX_LENGTH = FILTER_PARAM_PREFIX.length;
const PAGE_PARAM = 'page';

/**
 * @typedef {object} ActiveFilter
 * @property {string} field - Filter field name
 * @property {string} label - Filter display label
 * @property {string} value - Filter value
 * @property {string} displayValue - Value for display
 * @property {number} [count] - Result count (when available from API/facets)
 * @property {string} [swatchStyle] - Inline style for color swatch (when from color facet)
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} container - Container for active filter tags
 * @property {HTMLElement} clearAllButton - Clear all filters button
 * @property {HTMLElement} title - Applied filters title element
 */

class ConstructorActiveFacetsComponent extends Component {
  #controller = new AbortController();
  /** @type {HTMLElement | null} */
  #drawerClearAllButton = null;

  /**
   * Normalize refs that can be a single element or an array.
   * @template T
   * @param {T | T[] | undefined} ref
   * @returns {T | undefined}
   */
  #getRefElement(ref) {
    return Array.isArray(ref) ? ref[0] : ref;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Listen for results loaded to update from URL
    document.addEventListener('cnstrc:results-loaded', this.#handleResultsLoaded, { signal });

    // Optimistic update: show applied filters immediately when URL changes (before API returns)
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate, { signal });

    // Handle remove clicks (including Clear all inside this component)
    this.addEventListener('click', this.#handleClick, { signal });

    // Handle Clear all anywhere (e.g. drawer footer button)
    document.addEventListener('click', this.#handleDocumentClick, { signal });

    // Initial render from URL params
    this.#renderFromUrl();
  }

  /**
   * Handle document click for data-cnstrc-clear-all (e.g. drawer Clear all button)
   * @param {Event} event
   */
  #handleDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const clearAllTrigger = target.closest('[data-cnstrc-clear-all]');
    if (!clearAllTrigger) return;

    // Only one instance should handle each click. The drawer instance handles
    // clicks inside #filters-drawer; the non-drawer instance handles the rest.
    const drawer = clearAllTrigger.closest('#filters-drawer');
    const clickInDrawer = drawer != null;
    const isDrawerInstance = this.hasAttribute('data-drawer-context');
    if (clickInDrawer !== isDrawerInstance) return;

    this.#clearAllFilters(drawer instanceof HTMLElement ? drawer : undefined);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Optimistic update: render applied filters from current URL when filter change is dispatched
   */
  #handleFilterUpdate = () => {
    this.#renderFromUrl('optimistic');
  };

  /**
   * Handle results loaded event
   * @param {CustomEvent} event
   */
  #handleResultsLoaded = (event) => {
    const { facets } = event.detail;
    if (!facets) return;

    // Build filter summary from facets
    /** @type {ActiveFilter[]} */
    const activeFilters = [];

    for (const facet of facets) {
      const isColorFacet = isColorFacetField(facet.field);

      for (const value of facet.values) {
        if (!value.active) continue;
        activeFilters.push({
          field: facet.field,
          label: facet.label,
          value: value.value,
          displayValue: value.label,
          count: value.count,
          swatchStyle: isColorFacet
            ? getColorSwatchStyle(value, this.dataset.colorSwatchPrefix)
            : undefined,
        });
      }
    }

    const seenKeys = new Set(activeFilters.map((filter) => `${filter.field}\n${filter.value}`));
    const urlParams = new URLSearchParams(window.location.search);
    for (const [key, value] of urlParams) {
      if (!key.startsWith(FILTER_PARAM_PREFIX)) continue;
      const field = key.slice(FILTER_PARAM_PREFIX_LENGTH);
      const dedupeKey = `${field}\n${value}`;
      if (seenKeys.has(dedupeKey)) continue;
      seenKeys.add(dedupeKey);
      activeFilters.push({
        field,
        label: this.#formatLabel(field),
        value,
        displayValue: value,
      });
    }

    this.#renderActiveFilters(activeFilters, 'api');
  };

  /**
   * Render active filters from URL parameters
   * @param {string} [source] - 'optimistic' when called from FilterUpdate, 'init' on first load
   */
  #renderFromUrl(source = 'init') {
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {ActiveFilter[]} */
    const activeFilters = [];

    for (const [key, value] of urlParams) {
      if (!key.startsWith(FILTER_PARAM_PREFIX)) continue;

      const field = key.slice(FILTER_PARAM_PREFIX_LENGTH);
      activeFilters.push({
        field,
        label: this.#formatLabel(field),
        value,
        displayValue: value,
      });
    }

    this.#renderActiveFilters(activeFilters, source);
  }

  /**
   * Format field name as label
   * @param {string} field
   * @returns {string}
   */
  #formatLabel(field) {
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  /**
   * Render active filter tags
   * @param {ActiveFilter[]} filters
   * @param {string} [source] - 'optimistic' | 'api' | 'init' for performance logging
   */
  #renderActiveFilters(filters, source = 'api') {
    void source;
    const { container, clearAllButton, title } = this.refs;
    const containerEl = this.#getRefElement(container);
    const clearAllEl = this.#getRefElement(clearAllButton);
    const titleEl = this.#getRefElement(title);
    if (!containerEl) return;
    const filterCount = filters.length;
    const inDrawer = this.hasAttribute('data-drawer-context');

    // Update title with active filter count (e.g. "Applied filters (3)")
    const labelTemplate = this.dataset.appliedFiltersLabel;
    if (titleEl && labelTemplate) {
      titleEl.textContent = labelTemplate.replace('{count}', String(filterCount));
    }

    // Hide the whole block when no facets are applied
    this.toggleAttribute('hidden', filterCount === 0);

    // When in drawer, also show/hide the drawer footer Clear All button
    if (inDrawer) {
      // Cache the drawer clear-all element and refresh if the drawer re-renders.
      if (!this.#drawerClearAllButton?.isConnected) {
        const drawerClearAll = document.querySelector('#filters-drawer .cnstrc-drawer-clear-all');
        this.#drawerClearAllButton = drawerClearAll instanceof HTMLElement ? drawerClearAll : null;
      }
      const drawerClearAll = this.#drawerClearAllButton;
      if (drawerClearAll) {
        drawerClearAll.toggleAttribute('hidden', filterCount === 0);
      }
    }

    if (filterCount === 0) {
      containerEl.innerHTML = '';
      if (clearAllEl) {
        clearAllEl.toggleAttribute('hidden', true);
        containerEl.appendChild(clearAllEl);
      }
      return;
    }

    if (clearAllEl) {
      clearAllEl.toggleAttribute('hidden', false);
    }

    /** @type {string[]} */
    const pillsHtml = [];
    for (const filter of filters) {
      const isColorField = isColorFacetField(filter.field);
      const escapedField = escapeHtml(filter.field);
      const escapedValue = escapeHtml(filter.value);
      const escapedFilterLabel = escapeHtml(filter.label);
      const escapedDisplayValue = escapeHtml(filter.displayValue);
      const swatchStyle = isColorField
        ? filter.swatchStyle ||
          getColorSwatchStyle({ value: filter.value }, this.dataset.colorSwatchPrefix)
        : '';

      pillsHtml.push(`
        <span class="cnstrc-active-facets__tag" data-field="${escapedField}" data-value="${escapedValue}">
          ${isColorField && swatchStyle ? `<span class="cnstrc-active-facets__tag-swatch" style="${swatchStyle}" aria-hidden="true"></span>` : ''}
          <span class="cnstrc-active-facets__tag-value">${escapedDisplayValue}</span>
          <button
            type="button"
            class="cnstrc-active-facets__tag-remove"
            aria-label="Remove ${escapedFilterLabel}: ${escapedDisplayValue}"
            data-cnstrc-remove-filter
            data-field="${escapedField}"
            data-value="${escapedValue}"
          >
            <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </span>
      `);
    }

    containerEl.innerHTML = pillsHtml.join('');
    if (clearAllEl) {
      if (inDrawer) {
        containerEl.appendChild(clearAllEl);
      } else {
        containerEl.prepend(clearAllEl);
      }
    }

    const mark = performance.getEntriesByName('facet-filter-dispatched').pop();
    if (mark) {
      // Keep mark lifecycle without production logging noise.
      performance.clearMarks('facet-filter-dispatched');
    }
  }

  /**
   * Handle click events
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const removeButton = target.closest('[data-cnstrc-remove-filter]');
    if (removeButton) {
      event.stopPropagation();
      const field = removeButton.getAttribute('data-field');
      const value = removeButton.getAttribute('data-value');
      if (field != null && value != null) this.#removeFilter(field, value);
      return;
    }

    const clearAllButton = target.closest('[data-cnstrc-clear-all]');
    if (clearAllButton) {
      event.stopPropagation();
      const drawer = clearAllButton.closest('#filters-drawer');
      this.#clearAllFilters(drawer instanceof HTMLElement ? drawer : undefined);
    }
  };

  /**
   * Remove a single filter
   * @param {string} field
   * @param {string} value
   */
  #removeFilter(field, value) {
    const url = new URL(window.location.href);
    const { searchParams } = url;
    const paramName = `filter.${field}`;

    // Get all values for this param
    const values = searchParams.getAll(paramName);

    // Remove the specific value
    searchParams.delete(paramName);

    // Re-add non-removed values without creating an extra filtered array.
    for (const currentValue of values) {
      if (currentValue === value) continue;
      searchParams.append(paramName, currentValue);
    }

    searchParams.delete(PAGE_PARAM);

    history.pushState({}, '', url.toString());

    document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
  }

  /**
   * Clear all filters
   * @param {HTMLElement & { closeDialog?: () => Promise<void> } | undefined} [filtersDrawer] - When provided (e.g. click was inside #filters-drawer), close the drawer after clearing
   */
  #clearAllFilters(filtersDrawer) {
    const url = new URL(window.location.href);
    const { searchParams } = url;

    this.#clearAllFilterParams(url);

    searchParams.delete(PAGE_PARAM);

    history.pushState({}, '', url.toString());

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.FilterUpdate, {
        detail: { filters: [] },
      }),
    );

    if (filtersDrawer?.closeDialog) {
      filtersDrawer.closeDialog();
    }
  }

  /**
   * Remove all filter.* query params from a URL.
   * @param {URL} url
   */
  #clearAllFilterParams(url) {
    for (const key of new Set(url.searchParams.keys())) {
      if (key.startsWith(FILTER_PARAM_PREFIX)) url.searchParams.delete(key);
    }
  }
}

if (!customElements.get('constructor-active-facets')) {
  customElements.define('constructor-active-facets', ConstructorActiveFacetsComponent);
}

export { ConstructorActiveFacetsComponent };
